import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ResponsiveModal, ModalFooter, ModalClose } from "./ui/responsive-modal";
import { api } from "../App";

/**
 * BrainDumpSheet — full-screen multi-line jot capture.
 *
 * Behavior:
 *  - One thought per line (Enter = newline, Cmd+Enter = Save all)
 *  - Empty lines are skipped
 *  - 500 char max per line (matches backend)
 *  - Save posts each non-empty line as a separate jot
 *  - Emits `my-day-jots-updated` CustomEvent on success so /me can refetch
 *
 * Mobile: renders as bottom sheet via ResponsiveModal.
 * Desktop: renders as centered dialog.
 */
export default function BrainDumpSheet({ open, onOpenChange }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Focus after modal animation
      const t = setTimeout(() => textareaRef.current?.focus(), 200);
      return () => clearTimeout(t);
    } else {
      // Reset on close
      setText("");
      setSaving(false);
    }
  }, [open]);

  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const lineCount = lines.length;

  const save = async () => {
    if (lineCount === 0) return;
    setSaving(true);
    let succeeded = 0;
    let failed = 0;
    for (const line of lines) {
      try {
        await api.post("/me/jots", { text: line });
        succeeded++;
      } catch (err) {
        console.error("Jot create failed for:", line, err);
        failed++;
      }
    }
    setSaving(false);
    if (succeeded > 0) {
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(10);
      toast.success(`Added ${succeeded} jot${succeeded === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
      // Tell /me to refetch
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("my-day-jots-updated"));
      }
    } else if (failed > 0) {
      toast.error(`Failed to save ${failed} jot${failed === 1 ? "" : "s"}`);
    }
    setText("");
    onOpenChange(false);
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl + Enter → save all
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Brain dump"
      description="One thought per line · Cmd+Enter to save all"
      size="md"
    >
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="What's on your mind?&#10;&#10;One thought per line…"
        disabled={saving}
        rows={10}
        style={{
          width: "100%",
          minHeight: 200,
          background: "transparent",
          border: "none",
          color: "var(--t1)",
          fontFamily: "var(--font-sans)",
          fontSize: 16,
          lineHeight: 1.6,
          outline: "none",
          resize: "none",
          padding: "8px 4px",
          letterSpacing: "-0.005em",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: 12,
          borderTop: "1px solid var(--bdr)",
          marginTop: 8,
          fontSize: 12,
          color: "var(--t3)",
        }}
      >
        <span>
          {lineCount} line{lineCount === 1 ? "" : "s"}
          {lineCount > 0 && " · "}
          {lineCount > 0 && <span style={{ color: "var(--t2)" }}>Cmd+Enter saves all</span>}
        </span>
      </div>
      <ModalFooter>
        <ModalClose asChild>
          <button
            type="button"
            style={{
              padding: "10px 18px",
              border: "1px solid var(--bdr)",
              background: "var(--s2)",
              color: "var(--t2)",
              borderRadius: 999,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Cancel
          </button>
        </ModalClose>
        <button
          type="button"
          onClick={save}
          disabled={saving || lineCount === 0}
          style={{
            padding: "10px 20px",
            background: lineCount === 0 ? "var(--s3)" : "#e31937",
            border: "1px solid " + (lineCount === 0 ? "var(--bdr)" : "#e31937"),
            color: lineCount === 0 ? "var(--t3)" : "#fff",
            borderRadius: 999,
            cursor: lineCount === 0 || saving ? "not-allowed" : "pointer",
            fontSize: 14,
            fontWeight: 600,
            transition: "all .15s",
          }}
        >
          {saving ? "Saving…" : lineCount === 0 ? "Save" : `Save ${lineCount}`}
        </button>
      </ModalFooter>
    </ResponsiveModal>
  );
}
